<template lang="pug">

//- template editor always appears in slide-drawer
slide-drawer(
  v-model="isShow"
  ref="slideDrawerEditor"
  @input="setShowDrawerEdit(false)"
  :confirm-dismiss="confirmDismissEditor"
)
  template(slot="title") {{ formTitle }}
  template(slot="content")
    template-editor(
      ref="templateEditor"
      :description-error="descriptionError"
      :form-action="formAction"
      :is-creating-template="isCreatingTemplate"
      :is-editing="isEditing"
      :template-folders="templateFoldersList"
      :value="template"
      @cancel="closeSlideDrawer($event, 'slideDrawerEditor')"
      @content-valid="isTemplateContentValid = $event"
      @create-template="handleCreateTemplate"
      @input="confirmDismissEditor = true"
      @update-template="handleUpdateTemplate"
      style="width:640px"
    )
  template(slot="actionsCustom")
    .slider-actions.justify-end
      v-btn(
        flat
        color="error"
        data-cy="delete-template-button"
        @click="deleteTemplate(template)"
        v-show="!isEditing"
      ) Delete
      v-btn(
        flat
        color="primary"
        @click="setShowDrawerEdit(false)"
      ) Cancel
      v-btn.primary(
        data-cy="edit-template-button"
        @click="setEditing(!isEditing)"
        v-show="!isEditing"
      ) Edit
      v-btn.primary(
        :disabled="!isTemplateContentValid"
        v-show="isEditing"
        @click="$refs.templateEditor.save()"
        data-cy="save-template-button"
      ) Save Template

</template>
<script>

import {
  mapMutations,
  mapActions,
  mapState,
} from 'vuex';
import SlideDrawer from '@/Core/components/SlideDrawer';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import TemplateEditor from '@/Campaigns/components/TemplateEditor';
import extractTextFromHtml from '@/Text/utilities/extractTextFromHtml';

export default {
  components: {
    SlideDrawer,
    TemplateEditor,
  },
  mixins: [
    toggleSlideDrawer,
  ],
  props: {
    /**
    * The initial template value
    */
    value: {
      type: Object,
    },
  },
  data() {
    return {
      confirmDismissEditor: false,
      descriptionError: null,
      isTemplateContentValid: true,
      template: {},
      isUpdateTemplateFolders: true,
    };
  },

  created() {
    if (!this.folders || this.folders.length === 0) {
      this.getAllTemplateFolders(this.isUpdateTemplateFolders);
    }
    this.template = this.value || {};
  },
  computed: {
    ...mapState('templates', [
      'isEditing',
      'foldersFilters',
      'templateFolders',
      'showDrawerEdit',
      'templateToEdit',
    ]),

    isCreatingTemplate() {
      return !this.template.id;
    },

    formTitle() {
      return this.isCreatingTemplate ? 'New Template' : 'Template Details';
    },

    formAction() {
      return this.isCreatingTemplate ? 'create' : 'update';
    },

    templateFoldersList() {
      return this.foldersFilters.map(folder => {
        return { text: folder.name, value: folder.id };
      });
    },

    blankTemplate() {
      return {
        template_type: this.communicationType,
        body: '',
        files: [],
      };
    },

    isShow: {
      get() {
        return this.showDrawerEdit;
      },
      set(value) {
        this.setShowDrawerEdit(value);
      },
    },
  },

  watch: {
    /**
     * Opens the particular folder that the current selected template is in
     * Only works if we haven't input anything in the search bar (template-folder-level)
     *
     * When the template prop changes, set it to the template and show the editor.
     * Must keep the internal template as data so it may be edited
     */
    templateToEdit: {
      handler(value) {
        this.template = value;
        if (this.isCreatingTemplate) {
          this.setEditing(true);
        }
      },
      deep: true,
    },
  },

  methods: {
     ...mapMutations('templates', [
      'setShowDrawerEdit',
    ]),
    ...mapActions('templates', [
      'setEditing',
      'checkDuplicateTemplate',
      'findTemplateFolders',
      'getAllTemplateFolders'
    ]),
    ...mapActions('campaignAction', [
      'updateTemplate',
      'createTemplate',
    ]),

    setDesiredTemplate(desiredTemplate) {

      if (this.communicationType === 'Text') {
        // for text templates, strip out HTML tags in body
        // before sending desired template to parent component
        const textOnlyBody = extractTextFromHtml(desiredTemplate.body);
        this.$emit('change', Object.assign({}, desiredTemplate, { body: textOnlyBody }));
      } else {
        this.$emit('change', desiredTemplate);
      }
    },


    async handleUpdateTemplate(template) {

      const { description, id } = template;
      if (await this.isDuplicateDescription(description, id)) {
        return;
      }

      this.template = Object.assign({}, template);

      this.descriptionError = '';
      const updatedTemplate = await this.updateTemplate(this.template);

      this.setDesiredTemplate(updatedTemplate);
      this.closeSlideDrawer(false, 'slideDrawerEditor');
      this.template = this.blankTemplate;
      this.confirmDismissEditor = false;
    },


    async handleCreateTemplate(template) {
      const payload = {
        template_type: template.template_type,
        template_folder_id: template.template_folder_id,
        description: template.description,
        subject: template.subject,
        body: template.body,
        files: template.files,
      };

      if (await this.isDuplicateDescription(template.description, 0)) {
        return;
      }

      await this.createTemplate(payload);
      this.$emit('change', template);
      this.closeSlideDrawer(false, 'slideDrawerEditor');
      this.template = {};
      this.confirmDismissEditor = false;
    },


    async isDuplicateDescription(description, id) {
      try {
        await this.checkDuplicateTemplate({ description, id });
      } catch (error) {
        this.descriptionError = error.message;
        return true;
      }
      return false;
    },

    deleteTemplate(template) {
      this.$emit('deleteTemplate', template);
    },
  },
};
</script>
